import { Mail } from 'lucide-react';
import { useAuth } from './AuthContext';
import { Button } from '../components/atoms/Button';
import { env } from '../env';

/**
 * Login screen. Mirrors the two sign-in options on app.nyayassist.ai:
 *   - Google SSO (connection: env.auth0.googleConnection)
 *   - Email + password (connection: env.auth0.dbConnection)
 *
 * Connection names come from env because they differ per tenant (staging:
 * 'Username-Password-Authentication' / 'google-oauth2'; prod: 'NyayAssist' /
 * 'google-oauth2'). Hardcoding the staging DB name caused Auth0's
 * "the connection is not enabled" error on the prod tenant.
 *
 * Signup isn't handled inside the add-in — phone verification, terms, and
 * profile onboarding require a full-page flow. We link out to the web app
 * for that and support both tenants' login paths here.
 */
export function LoginScreen(): JSX.Element {
  const { state, login } = useAuth();
  const errorMessage = state.status === 'unauthenticated' ? state.error : undefined;
  const isBusy = state.status === 'loading';

  return (
    <div className="flex flex-col items-center justify-center h-full px-6 py-10 bg-background">
      <img
        src="/assets/logo-icon.png"
        alt="NyayAssist"
        className="w-14 h-14 mb-5"
        onError={(e) => {
          (e.currentTarget as HTMLImageElement).style.display = 'none';
        }}
      />
      <h1 className="text-xl font-semibold tracking-tight text-foreground mb-1">NyayAssist</h1>
      <p className="text-sm text-muted-foreground mb-8 text-center">
        Sign in to research and edit your document with NyayAssist AI.
      </p>

      <div className="w-full max-w-xs flex flex-col gap-2">
        <Button
          onClick={() => login(env.auth0.dbConnection)}
          disabled={isBusy}
          size="lg"
          className="w-full"
        >
          <Mail className="w-4 h-4" />
          <span className="ml-2">Continue with email</span>
        </Button>

        <div className="flex items-center gap-2 my-1 text-[11px] text-muted-foreground">
          <div className="flex-1 h-px bg-border" />
          <span>or</span>
          <div className="flex-1 h-px bg-border" />
        </div>

        <Button
          onClick={() => login(env.auth0.googleConnection)}
          disabled={isBusy}
          variant="outline"
          size="lg"
          className="w-full"
        >
          <GoogleIcon />
          <span className="ml-2">Continue with Google</span>
        </Button>
      </div>

      {errorMessage && (
        <p className="mt-4 text-xs text-red-600 text-center max-w-xs">{errorMessage}</p>
      )}

      <p className="mt-8 text-xs text-muted-foreground text-center">
        Don&apos;t have an account? Sign up at{' '}
        <a
          href="https://app.nyayassist.ai"
          target="_blank"
          rel="noreferrer"
          className="text-primary underline"
        >
          app.nyayassist.ai
        </a>
      </p>
    </div>
  );
}

function GoogleIcon(): JSX.Element {
  // Google "G" logo (official colors). Inline SVG so we don't depend on a CDN.
  return (
    <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.6 20.1H42V20H24v8h11.3C33.7 32.2 29.3 35 24 35c-6.1 0-11-4.9-11-11s4.9-11 11-11c2.8 0 5.4 1.1 7.4 2.8l5.7-5.7C33.6 6.8 29 5 24 5 13.5 5 5 13.5 5 24s8.5 19 19 19c10.5 0 19-8.5 19-19 0-1.3-.1-2.6-.4-3.9z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.6 16.2 19 13 24 13c2.8 0 5.4 1.1 7.4 2.8l5.7-5.7C33.6 6.8 29 5 24 5 16.6 5 10.2 9.2 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 43c5 0 9.5-1.9 12.9-5l-6-5.2c-1.8 1.4-4.2 2.2-6.9 2.2-5.3 0-9.7-2.7-11.3-7l-6.5 5C9.7 38.6 16.2 43 24 43z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.1H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.6l6 5.2c3.4-3.2 5.8-7.8 5.8-14.8 0-1.3-.1-2.6-.4-3.9z"
      />
    </svg>
  );
}
